import React, { useState, useEffect } from "react";
import theme from "../../theme";

const SynthPairCreator = ({
  selectedAccount,
  // Ready-made list of legs. The scanner-only MONITOR account is unknown to the
  // IB backend, so /debug/positions answers nothing for it — a caller that
  // already knows its instruments hands them over instead.
  assets = null,
  onPairCreated,
  onClose,
  existingPairs,
  onPairDeleted,
}) => {
  const [availableAssets, setAvailableAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [synthPairs, setSynthPairs] = useState([]);
  const [loadingPairs, setLoadingPairs] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [showPairPicker, setShowPairPicker] = useState(false);
  const [editingPair, setEditingPair] = useState(null);

  const emptyForm = {
    pairSymbol: "",
    firstAsset: null,
    firstMultiplier: 1,
    firstFollowsSignal: true,
    secondAsset: null,
    secondMultiplier: 1,
    secondFollowsSignal: false,
    basePosition: 0.0,
    isAssetActive: true,
  };

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Only a leg that is switched on can be traded, but a caller-supplied list
    // need not carry the flags at all — absent is not "off".
    const usable = (list) => (list || []).filter(
      (asset) => asset.isAssetActive !== false && asset.isBlackList !== true,
    );

    const loadAssets = async () => {
      if (assets) {
        setAvailableAssets(usable(assets));
        setLoadingAssets(false);
        return;
      }
      if (!selectedAccount) return;

      try {
        const { fetchDeepAssets } = await import("../../services/api");
        setAvailableAssets(usable(await fetchDeepAssets(
          selectedAccount.broker,
          selectedAccount.account,
        )));
      } catch (error) {
        console.error("Error loading assets:", error);
        setAvailableAssets([]);
      } finally {
        setLoadingAssets(false);
      }
    };

    loadAssets();
    loadSynthPairs();
  }, [selectedAccount, assets]);

  const loadSynthPairs = async () => {
    if (!selectedAccount) return;

    setLoadingPairs(true);
    try {
      const { getSynthPairs } = await import("../../services/api");
      const pairs = await getSynthPairs(
        selectedAccount.broker,
        selectedAccount.account,
      );
      setSynthPairs(pairs || []);
    } catch (error) {
      console.error("Error loading synthetic pairs:", error);
      setSynthPairs([]);
    } finally {
      setLoadingPairs(false);
    }
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case "pairSymbol":
        if (!value || !value.includes("/")) {
          newErrors.pairSymbol =
            'Pair symbol must contain "/" (e.g., 2PL/3MNQ)';
        } else if (value.length > 50) {
          newErrors.pairSymbol = "Pair symbol must be 50 characters max";
        } else {
          delete newErrors.pairSymbol;
        }
        break;
      case "firstAsset":
      case "secondAsset":
        if (!value) {
          newErrors[name] = "Asset selection is required";
        } else if (!availableAssets.some((a) => a.symbol === value.symbol)) {
          // The field is a free-text search box, so half-typed text reaches here.
          newErrors[name] = "Pick an asset from the list";
        } else {
          delete newErrors[name];
        }
        break;
      case "firstMultiplier":
      case "secondMultiplier":
        if (value <= 0) {
          newErrors[name] = "Multiplier must be greater than 0";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const autofillPairSymbol = () => {
    if (formData.firstAsset && formData.secondAsset) {
      const pairSymbol = `${formData.firstAsset.symbol}/${formData.secondAsset.symbol}`;
      handleInputChange("pairSymbol", pairSymbol);
    }
  };

  const getAssetDisplayName = (asset) => {
    if (!asset) return "";
    let name = asset.symbol;
    if (selectedAccount.broker === "ib") {
      name = `${asset.symbol} (${asset.sectype || "N/A"})`;
    }
    return name;
  };

  const handleOpenPairPicker = () => {
    setShowPairPicker(true);
  };

  const handleSelectPairForEdit = (pair) => {
    const firstAssetObj = availableAssets.find(
      (a) => a.symbol === pair.first_symbol,
    ) || { symbol: pair.first_symbol };
    const secondAssetObj = availableAssets.find(
      (a) => a.symbol === pair.second_symbol,
    ) || { symbol: pair.second_symbol };

    const firstMultRaw = pair.first_multiplier;
    const secondMultRaw = pair.second_multiplier;

    setEditingPair(pair);
    setFormData({
      pairSymbol: pair.pair_symbol,
      firstAsset: firstAssetObj,
      firstMultiplier: Math.abs(firstMultRaw),
      firstFollowsSignal: firstMultRaw >= 0,
      secondAsset: secondAssetObj,
      secondMultiplier: Math.abs(secondMultRaw),
      secondFollowsSignal: secondMultRaw >= 0,
      basePosition: pair.base_position || 0,
      isAssetActive:
        pair.isAssetActive !== undefined ? pair.isAssetActive : true,
    });
    setErrors({});
    setShowPairPicker(false);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingPair(null);
    setFormData(emptyForm);
    setErrors({});
  };

  const buildDataToSend = () => ({
    pair_symbol: formData.pairSymbol.trim(),
    broker: selectedAccount.broker,
    account: selectedAccount.broker === "ib" ? selectedAccount.account : null,
    first_symbol: formData.firstAsset.symbol,
    first_multiplier: formData.firstFollowsSignal
      ? Math.abs(formData.firstMultiplier)
      : -Math.abs(formData.firstMultiplier),
    second_symbol: formData.secondAsset.symbol,
    second_multiplier: formData.secondFollowsSignal
      ? Math.abs(formData.secondMultiplier)
      : -Math.abs(formData.secondMultiplier),
    base_position: formData.basePosition,
    isAssetActive: formData.isAssetActive,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = ["pairSymbol", "firstAsset", "secondAsset"];
    const allValid = requiredFields.every((field) => {
      const isValid = validateField(field, formData[field]);
      return isValid && formData[field];
    });

    if (!allValid || Object.keys(errors).length > 0) {
      alert("Please fill all required fields and fix validation errors");
      return;
    }

    if (formData.firstAsset.symbol === formData.secondAsset.symbol) {
      alert("First and second assets must be different");
      return;
    }

    setLoading(true);

    try {
      const { createSynthPair, deleteSynthPair } =
        await import("../../services/api");

      if (editMode && editingPair) {
        const deleted = await deleteSynthPair(editingPair.id);
        if (!deleted) {
          alert("Failed to delete old pair before updating");
          return;
        }
      }

      const result = await createSynthPair(buildDataToSend());

      if (result) {
        alert(
          editMode
            ? "Synthetic pair updated successfully!"
            : "Synthetic pair created successfully!",
        );
        await loadSynthPairs();
        onPairCreated && onPairCreated();
        handleCancelEdit();
      } else {
        alert(
          editMode
            ? "Failed to update synthetic pair"
            : "Failed to create synthetic pair",
        );
      }
    } catch (error) {
      console.error("Error saving synthetic pair:", error);
      alert(
        "Failed to save synthetic pair: " +
          (error.response?.data?.detail || error.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pairId, pairSymbol) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the synthetic pair "${pairSymbol}"?`,
      )
    )
      return;

    setLoading(true);

    try {
      const { deleteSynthPair } = await import("../../services/api");
      const result = await deleteSynthPair(pairId);

      if (result) {
        alert("Synthetic pair deleted successfully!");
        await loadSynthPairs(); // Refresh the pairs list
        onPairDeleted && onPairDeleted();
      } else {
        alert("Failed to delete synthetic pair");
      }
    } catch (error) {
      console.error("Error deleting synthetic pair:", error);
      alert(
        "Failed to delete synthetic pair: " +
          (error.response?.data?.detail || error.message),
      );
    } finally {
      setLoading(false);
    }
  };

  if (showPairPicker) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className={`w-full max-w-md p-5 ${theme.modalBg}`}>
          <h3 className={`mb-1 text-sm font-bold ${theme.textPrimary}`}>Edit synthetic pair</h3>
          <p className={`mb-4 text-xs ${theme.textMuted}`}>
            Select the pair you want to edit
          </p>

          {loadingPairs ? (
            <div className="flex justify-center items-center h-20">
              <div className={`h-6 w-6 animate-spin rounded-full border-2 ${theme.spinner}`} />
              <span className={`ml-2 text-xs ${theme.textMuted}`}>Loading pairs…</span>
            </div>
          ) : synthPairs.length === 0 ? (
            <div className={`rounded-xl ${theme.sectionBg} py-8 text-center text-xs ${theme.textMuted}`}>
              No synthetic pairs found for this account
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {synthPairs.map((pair) => (
                <button
                  key={pair.id}
                  type="button"
                  onClick={() => handleSelectPairForEdit(pair)}
                  className={`w-full rounded-xl ${theme.cardBorder} p-3 text-left transition-colors hover:bg-olive-50`}
                >
                  <span className={`text-sm font-semibold ${theme.textSecondary}`}>
                    {pair.pair_symbol}
                  </span>
                  <div className={`mt-0.5 text-[10px] ${theme.textMuted}`}>
                    <span>
                      {pair.first_symbol} ×{Math.abs(pair.first_multiplier)}{" "}
                      {pair.first_multiplier >= 0 ? "↑ Signal" : "↓ Opposite"}
                    </span>
                    <span className="mx-2 text-olive-200">|</span>
                    <span>
                      {pair.second_symbol} ×{Math.abs(pair.second_multiplier)}{" "}
                      {pair.second_multiplier >= 0 ? "↑ Signal" : "↓ Opposite"}
                    </span>
                    {pair.base_position > 0 && (
                      <span className="ml-2">
                        (Base: {pair.base_position})
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => setShowPairPicker(false)}
              className={`rounded-xl px-4 py-2 text-xs font-bold ${theme.btnGhost}`}
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`max-h-[90vh] w-full max-w-2xl overflow-y-auto p-5 ${theme.modalBg}`}>
        {/* Native typeahead over the whole list — no filter state to keep. */}
        <datalist id="synth-pair-assets">
          {availableAssets.map((asset) => (
            <option key={`${asset.symbol}-${asset.sectype || ""}`} value={asset.symbol}>
              {getAssetDisplayName(asset)}
            </option>
          ))}
        </datalist>
        <h3 className={`mb-4 text-sm font-bold ${theme.textPrimary}`}>
          {editMode
            ? `Edit Pair — ${editingPair?.pair_symbol}`
            : "Synthetic Pair Manager"}
        </h3>

        {!editMode && (
          <>
            {loadingPairs ? (
              <div className="mb-6">
                <h4 className={`mb-3 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>
                  Existing Synthetic Pairs
                </h4>
                <div className={`flex h-16 items-center justify-center rounded-xl ${theme.sectionBg}`}>
                  <div className={`h-6 w-6 animate-spin rounded-full border-2 ${theme.spinner}`} />
                  <span className={`ml-2 text-xs ${theme.textMuted}`}>Loading pairs…</span>
                </div>
                <hr className="my-4 border-olive-100" />
              </div>
            ) : synthPairs && synthPairs.length > 0 ? (
              <div className="mb-6">
                <h4 className={`mb-3 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>
                  Existing Synthetic Pairs
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {synthPairs.map((pair) => (
                    <div
                      key={pair.id}
                      className={`flex items-center justify-between rounded-xl ${theme.cardBorder} ${theme.sectionBg} p-3`}
                    >
                      <div className="flex-1">
                        <span className={`text-sm font-semibold ${theme.textSecondary}`}>
                          {pair.pair_symbol}
                        </span>
                        <div className={`mt-1 text-[10px] ${theme.textMuted}`}>
                          <span>
                            {pair.first_symbol} ×{" "}
                            {Math.abs(pair.first_multiplier)}{" "}
                            {pair.first_multiplier > 0
                              ? "(Signal direction)"
                              : "(Opposite direction)"}
                          </span>
                          <span className="mx-1">|</span>
                          <span>
                            {pair.second_symbol} ×{" "}
                            {Math.abs(pair.second_multiplier)}{" "}
                            {pair.second_multiplier > 0
                              ? "(Signal direction)"
                              : "(Opposite direction)"}
                          </span>
                          {pair.base_position > 0 && (
                            <span className="ml-2">
                              (Base: {pair.base_position})
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(pair.id, pair.pair_symbol)}
                        disabled={loading}
                        className={`rounded-lg px-2.5 py-1 text-[10px] font-bold ${theme.btnDanger} disabled:opacity-40`}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
                <hr className="my-4 border-olive-100" />
              </div>
            ) : (
              <div className="mb-6">
                <h4 className={`mb-3 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>
                  Existing Synthetic Pairs
                </h4>
                <div className={`rounded-xl ${theme.sectionBg} py-4 text-center text-xs ${theme.textMuted}`}>
                  No synthetic pairs found for this account
                </div>
                <hr className="my-4 border-olive-100" />
              </div>
            )}

            <div className="mb-6 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div>
                <p className="text-xs font-bold text-amber-800">
                  Edit existing pair
                </p>
                <p className="mt-0.5 text-[10px] text-amber-600">
                  Select a pair to modify its settings
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenPairPicker}
                disabled={synthPairs.length === 0}
                className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-amber-600 disabled:opacity-40"
              >
                EDIT PAIR
              </button>
            </div>
          </>
        )}

        {editMode && (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center">
              <span className="text-amber-600 mr-2">✏️</span>
              <div>
                <p className="text-xs font-bold text-amber-800">Edit mode</p>
                <p className="text-[10px] text-amber-600">
                  The old pair will be replaced when you save
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-[10px] font-bold text-amber-700 underline hover:text-amber-900"
            >
              CANCEL EDIT
            </button>
          </div>
        )}

        <h4 className={`mb-3 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>
          {editMode ? "Modify Pair Settings" : "Create New Synthetic Pair"}
        </h4>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Pair Symbol */}
          <div>
            <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>
              Pair Symbol *
            </label>
            <div className="flex">
              <input
                type="text"
                value={formData.pairSymbol}
                onChange={(e) =>
                  handleInputChange("pairSymbol", e.target.value)
                }
                className={`flex-1 w-full rounded-xl border px-3 py-2 text-sm text-olive-900 outline-none focus:ring-2 focus:ring-olive-300 ${errors.pairSymbol ? "border-red-400" : "border-olive-200"}`}
                required
                placeholder="e.g., 2PL/3MNQ"
              />
              <button
                type="button"
                onClick={autofillPairSymbol}
                className={`ml-2 rounded-xl px-3 py-2 text-xs font-bold ${theme.btnSecondary} disabled:opacity-40`}
                disabled={!formData.firstAsset || !formData.secondAsset}
              >
                AUTO
              </button>
            </div>
            {errors.pairSymbol && (
              <p className="mt-1 text-[10px] font-bold text-red-500">{errors.pairSymbol}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Element */}
            <div className={`rounded-xl ${theme.cardBorder} p-4`}>
              <h4 className={`mb-3 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>First Element</h4>

              <div className="space-y-3">
                <div>
                  <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>
                    Asset *
                  </label>
                  {loadingAssets ? (
                    <div className={`text-xs ${theme.textMuted}`}>Loading assets…</div>
                  ) : (
                    <input
                      list="synth-pair-assets"
                      value={formData.firstAsset?.symbol || ""}
                      placeholder="Type to search…"
                      onChange={(e) => {
                        const asset = availableAssets.find(
                          (a) => a.symbol === e.target.value,
                        );
                        // The typed text is kept as a symbol so the field can be
                        // edited; anything that is not an asset stays invalid.
                        handleInputChange(
                          "firstAsset",
                          asset || (e.target.value ? { symbol: e.target.value } : null),
                        );
                      }}
                      className={`w-full rounded-xl border px-3 py-2 text-sm text-olive-900 outline-none focus:ring-2 focus:ring-olive-300 ${errors.firstAsset ? "border-red-400" : "border-olive-200"}`}
                    />
                  )}
                  {errors.firstAsset && (
                    <p className="mt-1 text-[10px] font-bold text-red-500">
                      {errors.firstAsset}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>
                    Multiplier
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={formData.firstMultiplier}
                    onChange={(e) =>
                      handleInputChange(
                        "firstMultiplier",
                        parseFloat(e.target.value),
                      )
                    }
                    className={`w-full rounded-xl border px-3 py-2 text-sm text-olive-900 outline-none focus:ring-2 focus:ring-olive-300 ${errors.firstMultiplier ? "border-red-400" : "border-olive-200"}`}
                    required
                  />
                  {errors.firstMultiplier && (
                    <p className="mt-1 text-[10px] font-bold text-red-500">
                      {errors.firstMultiplier}
                    </p>
                  )}
                </div>

                <div className={`mt-4 flex items-center justify-between rounded-xl ${theme.cardBorder} ${theme.sectionBg} p-2`}>
                  <span className={`text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>Direction</span>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${!formData.firstFollowsSignal ? "bg-red-50 text-red-500" : theme.textMuted}`}
                    >
                      Opposite
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleInputChange(
                          "firstFollowsSignal",
                          !formData.firstFollowsSignal,
                        )
                      }
                      className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-olive-300 transition-colors duration-200 ease-in-out focus:outline-none"
                    >
                      <span
                        className={`${
                          formData.firstFollowsSignal
                            ? "translate-x-5"
                            : "translate-x-0"
                        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${formData.firstFollowsSignal ? "bg-emerald-50 text-emerald-600" : theme.textMuted}`}
                    >
                      Signal
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Element */}
            <div className={`rounded-xl ${theme.cardBorder} p-4`}>
              <h4 className={`mb-3 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>Second Element</h4>

              <div className="space-y-3">
                <div>
                  <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>
                    Asset *
                  </label>
                  {loadingAssets ? (
                    <div className={`text-xs ${theme.textMuted}`}>Loading assets…</div>
                  ) : (
                    <input
                      list="synth-pair-assets"
                      value={formData.secondAsset?.symbol || ""}
                      placeholder="Type to search…"
                      onChange={(e) => {
                        const asset = availableAssets.find(
                          (a) => a.symbol === e.target.value,
                        );
                        // The typed text is kept as a symbol so the field can be
                        // edited; anything that is not an asset stays invalid.
                        handleInputChange(
                          "secondAsset",
                          asset || (e.target.value ? { symbol: e.target.value } : null),
                        );
                      }}
                      className={`w-full rounded-xl border px-3 py-2 text-sm text-olive-900 outline-none focus:ring-2 focus:ring-olive-300 ${errors.secondAsset ? "border-red-400" : "border-olive-200"}`}
                    />
                  )}
                  {errors.secondAsset && (
                    <p className="mt-1 text-[10px] font-bold text-red-500">
                      {errors.secondAsset}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>
                    Multiplier
                  </label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={formData.secondMultiplier}
                    onChange={(e) =>
                      handleInputChange(
                        "secondMultiplier",
                        parseFloat(e.target.value),
                      )
                    }
                    className={`w-full rounded-xl border px-3 py-2 text-sm text-olive-900 outline-none focus:ring-2 focus:ring-olive-300 ${errors.secondMultiplier ? "border-red-400" : "border-olive-200"}`}
                    required
                  />
                  {errors.secondMultiplier && (
                    <p className="mt-1 text-[10px] font-bold text-red-500">
                      {errors.secondMultiplier}
                    </p>
                  )}
                </div>

                <div className={`mt-4 flex items-center justify-between rounded-xl ${theme.cardBorder} ${theme.sectionBg} p-2`}>
                  <span className={`text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>Direction</span>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${!formData.secondFollowsSignal ? "bg-red-50 text-red-500" : theme.textMuted}`}
                    >
                      Opposite
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleInputChange(
                          "secondFollowsSignal",
                          !formData.secondFollowsSignal,
                        )
                      }
                      className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-olive-300 transition-colors duration-200 ease-in-out focus:outline-none"
                    >
                      <span
                        className={`${
                          formData.secondFollowsSignal
                            ? "translate-x-5"
                            : "translate-x-0"
                        } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </button>
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-bold transition-colors ${formData.secondFollowsSignal ? "bg-emerald-50 text-emerald-600" : theme.textMuted}`}
                    >
                      Signal
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Base Position */}
          <div>
            <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wide ${theme.textMuted}`}>
              Base Position
            </label>
            <input
              type="number"
              step="1"
              value={formData.basePosition}
              onChange={(e) =>
                handleInputChange(
                  "basePosition",
                  parseFloat(e.target.value) || 0,
                )
              }
              className={`w-full rounded-xl border px-3 py-2 text-sm text-olive-900 outline-none focus:ring-2 focus:ring-olive-300 border-olive-200`}
              placeholder="0.0"
            />
          </div>

          {/* Asset Active */}
          <div>
            <label className={`flex cursor-pointer items-center gap-2 text-xs font-bold ${theme.textSecondary}`}>
              <input
                type="checkbox"
                checked={formData.isAssetActive}
                onChange={(e) =>
                  handleInputChange("isAssetActive", e.target.checked)
                }
                className="h-3.5 w-3.5 accent-olive-500"
              />
              ASSET ACTIVE
            </label>
          </div>

          {/* Trading Logic Explanation */}
          <div className={`mt-4 rounded-xl ${theme.sectionBg} p-3`}>
            <h4 className={`mb-2 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>Trading logic</h4>
            <div className={`text-xs ${theme.textSecondary}`}>
              <p>
                • <b>Signal</b>: Trades in the same direction as the signal
                (e.g. Signal BUY → Action BUY)
              </p>
              <p>
                • <b>Opposite</b>: Trades in the opposite direction (e.g. Signal
                BUY → Action SELL)
              </p>
              <p>• Quantities calculated as: base_position × multiplier</p>
              <p className={`mt-2 text-[10px] ${theme.textMuted}`}>
                • Exchange, currency, and sectype are retrieved dynamically from
                each asset's configuration when trading
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={editMode ? handleCancelEdit : onClose}
              className={`rounded-xl px-4 py-2 text-xs font-bold ${theme.btnGhost}`}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`rounded-xl px-4 py-2 text-xs font-bold disabled:opacity-40 ${editMode ? "bg-amber-500 text-white hover:bg-amber-600" : theme.btnPrimary}`}
              disabled={
                loading ||
                !formData.pairSymbol ||
                !formData.firstAsset ||
                !formData.secondAsset ||
                Object.keys(errors).length > 0
              }
            >
              {loading
                ? "…"
                : editMode
                  ? "SAVE CHANGES"
                  : "CREATE PAIR"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SynthPairCreator;
